import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";
import classNames from "classnames";
import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";
import { ReplayIcon } from "@jti/brand-icons/replay";
import { Button } from "../../../../components/Button";
import { Checkbox } from "../../../../components/Checkbox/Checkbox";
import { DropdownSelect } from "../../../../components/Inputs";
import { useStoreLocatorContext } from "../../../../components/StoreLocator/hooks";
import { useTranslations } from "../../../../providers/useTranslations";
import { StoreLocatorFilterButton } from "../StoreLocatorFilterButton";
import styles from "./StoreLocatorFilters.module.scss";
const MAX_MOBILE_WIDTH = 479;
export const OPEN_NOW_VAL = "open_now";
const OPEN_NOW_FILTER_OBJ = { key: OPEN_NOW_VAL, name: OPEN_NOW_VAL };
export const StoreLocatorFilters = () => {
    const { __ } = useTranslations();
    const { selectedFilters, filters, filtersEnabled, handleChangeFilters, handleResetFilters, handleToggleFilters, isFiltersAvailable, isFiltersOpened, isOpenNowFilterEnabled, } = useStoreLocatorContext();
    const [activeFilters, setActiveFilters] = useState({});
    const filtersRef = useRef(activeFilters);
    const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH });
    const selectedFiltersNumber = useMemo(() => (selectedFilters ? Object.values(selectedFilters)?.filter(({ selected }) => selected).length : 0), [selectedFilters]);
    const applyFilters = (categoryToSkip) => {
        let currentFilters = {};
        Object.keys(activeFilters).forEach((category) => {
            if (category === categoryToSkip)
                return;
            activeFilters[category].forEach((filter) => {
                currentFilters = {
                    ...currentFilters,
                    [`${category}.${filter.key}`]: {
                        key: filter.key,
                        selected: true,
                        category,
                    },
                };
            });
        });
        handleChangeFilters(currentFilters);
    };
    const changeFilters = (category, filter) => {
        setActiveFilters((prev) => {
            let changedCategory = [...(prev[category] || [])];
            if (changedCategory.some((f) => f.key === filter.key)) {
                changedCategory = changedCategory.filter((f) => f.key !== filter.key);
            }
            else {
                changedCategory.push(filter);
            }
            return {
                ...prev,
                [category]: changedCategory,
            };
        });
    };
    const checkFiltersStatus = useCallback((category, filterKey) => {
        return activeFilters[category]?.some((f) => f.key === filterKey) || false;
    }, [activeFilters]);
    useEffect(() => {
        if (selectedFilters) {
            const activeFilters = {};
            Object.keys(selectedFilters).forEach((kk) => {
                const [categoryKey, filterKey] = kk.split(".");
                if (categoryKey === OPEN_NOW_VAL) {
                    activeFilters[categoryKey] = [OPEN_NOW_FILTER_OBJ];
                    return;
                }
                if (selectedFilters[kk]?.selected) {
                    const filter = filters.find((fc) => fc.key === categoryKey)?.filters.find((f) => f.key === filterKey);
                    if (activeFilters[categoryKey]) {
                        activeFilters[categoryKey].push(filter);
                    }
                    else {
                        activeFilters[categoryKey] = [filter];
                    }
                }
            });
            setActiveFilters(activeFilters);
        }
    }, [selectedFilters]);
    useEffect(() => {
        if (isMobile || !isOpenNowFilterEnabled)
            return;
        if (activeFilters[OPEN_NOW_VAL]?.length !== filtersRef.current[OPEN_NOW_VAL]?.length) {
            applyFilters();
        }
        filtersRef.current = activeFilters;
    }, [activeFilters, isMobile]);
    const handleDropdownSelect = (categoryKey, _, e) => {
        if (e.option)
            changeFilters(categoryKey, e.option);
    };
    const handleDropdownRestart = (categoryKey) => {
        applyFilters(categoryKey);
    };
    if (!isFiltersAvailable || !filtersEnabled) {
        return null;
    }
    if (isMobile) {
        return (_jsxs("div", { children: [_jsx(StoreLocatorFilterButton, { isFiltersAvailable: isFiltersAvailable, isFiltersOpened: isFiltersOpened, onToggleFilters: handleToggleFilters, selectedFiltersNumber: selectedFiltersNumber }), isFiltersOpened &&
                    createPortal(_jsxs("div", { className: styles.filtersMobileWrapper, children: [_jsx("div", { className: styles.filtersHeaderContainer, children: _jsxs("button", { className: styles.filtersHeaderButton, onClick: handleToggleFilters, children: [_jsx(ChevronBackwardIcon, {}), _jsx("h2", { children: __("headerlinks.button.findstore") })] }) }), _jsxs("div", { className: styles.filtersTitleContainer, children: [_jsx("h2", { className: styles.filtersTitle, "data-testid": "storeLocatorFiltersTitle", children: __("storelocator.filtersby") }), !!selectedFiltersNumber && (_jsxs("button", { className: styles.resetButton, onClick: handleResetFilters, "aria-label": __("storelocator.reset_filters"), children: [_jsx(ReplayIcon, { className: styles.resetButtonIcon, height: "20px", width: "20px", "aria-hidden": "true" }), __("storelocator.reset_filters")] }))] }), _jsxs("div", { className: styles.filtersList, children: [isOpenNowFilterEnabled && (_jsxs("div", { className: styles.filtersCategory, children: [_jsx("h3", { className: styles.filtersCategoryTitle, children: "Store hours" }), _jsx(Checkbox, { labelHtml: __("store_locator.popup.shop_status.open"), onChange: () => {
                                                    changeFilters(OPEN_NOW_VAL, OPEN_NOW_FILTER_OBJ);
                                                }, value: { checked: checkFiltersStatus(OPEN_NOW_VAL, OPEN_NOW_VAL) } })] })), filters?.map((filterCategory) => (_jsxs("div", { className: styles.filtersCategory, children: [_jsx("div", { className: styles.filtersCategoryTitle, children: filterCategory.name }), _jsx("div", { className: styles.filtersCategoryItems, children: filterCategory.filters.map((filter) => (_jsx("div", { className: styles.filtersCategoryItem, children: _jsx(Checkbox, { onChange: () => {
                                                            changeFilters(filterCategory.key, filter);
                                                        }, value: { checked: checkFiltersStatus(filterCategory.key, filter.key) }, className: styles.checkboxContainer, labelHtml: filter.name }) }, filter.key))) })] }, filterCategory.name)))] }), _jsx("div", { className: styles.filterButtonContainer, children: _jsx(Button, { className: styles.applyButton, color: "primary", size: "small", onClick: () => {
                                        applyFilters();
                                    }, children: __("storelocator.button.apply") }) })] }), document.body)] }));
    }
    return (_jsxs("div", { className: styles.filtersWrapper, "data-testid": "storeLocatorFilters", children: [_jsxs("div", { className: styles.filtersContainer, children: [_jsx("h2", { className: styles.filtersTitle, "data-testid": "storeLocatorFiltersTitle", children: __("storelocator.filtersby") }), isOpenNowFilterEnabled && (_jsx(Checkbox, { labelHtml: __("store_locator.popup.shop_status.open"), onChange: () => {
                            changeFilters(OPEN_NOW_VAL, OPEN_NOW_FILTER_OBJ);
                        }, value: { checked: checkFiltersStatus(OPEN_NOW_VAL, OPEN_NOW_VAL) } })), _jsx("div", { className: classNames(styles.dropdownSelectContainer), children: filters?.map((filterCategory, i) => {
                            return (_jsx(DropdownSelect, { dataTestId: `storeLocatorFiltersDropdown-${i}`, containerClassName: styles.dropdownSelect, options: filterCategory.filters, label: filterCategory.name, onChange: handleDropdownSelect.bind(this, filterCategory.key), onRestart: handleDropdownRestart.bind(this, filterCategory.key), value: activeFilters[filterCategory.key] || [], onApply: applyFilters, applyButtonText: __("storelocator.button.apply"), restartButtonText: __("storelocator.button.cancel") }, filterCategory.key));
                        }) })] }), !!selectedFiltersNumber && (_jsxs("button", { className: classNames(styles.resetButton, styles.resetButtonMobile), onClick: handleResetFilters, children: [_jsx(ReplayIcon, { width: "20px", height: "20px" }), __("storelocator.reset_filters")] }))] }));
};
//# sourceMappingURL=StoreLocatorFilters.js.map